import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, FileText, Image, Users, ClipboardList, ExternalLink, Settings, BarChart2, Eye, TrendingUp, Calendar } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import ArticlesAdmin from "@/pages/admin/ArticlesAdmin";
import GalleryAdmin from "@/pages/admin/GalleryAdmin";
import OrgAdmin from "@/pages/admin/OrgAdmin";
import RegistrationAdmin from "@/pages/admin/RegistrationAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";

const LOGO = "https://customer-assets.emergentagent.com/job_tozan-modern/artifacts/febczm58_Logo%20Website.png";

const TABS = [
  { key: "statistik",   label: "Statistik",           icon: BarChart2 },
  { key: "artikel",     label: "Artikel",              icon: FileText },
  { key: "galeri",      label: "Galeri Foto",          icon: Image },
  { key: "organisasi",  label: "Struktur Organisasi", icon: Users },
  { key: "pendaftaran", label: "Pendaftaran",          icon: ClipboardList },
  { key: "pengaturan",  label: "Pengaturan Situs",     icon: Settings },
];

// ===== Statistik Pengunjung =====
function StatistikTab() {
  const [visitors, setVisitors]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get("/visitors")
      .then((res) => setVisitors(res.data))
      .catch(() => setVisitors(null))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) return <p className="text-gray-400 py-8 text-center">Memuat statistik...</p>;
  if (!visitors) return <p className="text-red-400 py-8 text-center">Gagal memuat data statistik.</p>;

  const cards = [
    { label: "Total Pengunjung",    value: visitors.total?.toLocaleString("id-ID") || "0", icon: Eye,       color: "#198530" },
    { label: "Pengunjung Hari Ini", value: visitors.today?.toLocaleString("id-ID") || "0", icon: TrendingUp, color: "#d4af37" },
    { label: "Terakhir Diperbarui", value: visitors.last_date || "-",                      icon: Calendar,   color: "#7c3aed" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Statistik Pengunjung</h2>
        <button
          onClick={() => setRefreshKey((k) => k + 1)}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
          style={{ backgroundColor: "#198530" }}
        >
          Refresh
        </button>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <div key={idx} className="bg-white rounded-xl p-5" style={{ border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${card.color}15` }}>
                  <Icon size={20} style={{ color: card.color }} />
                </div>
                <p className="text-sm text-gray-500 font-medium">{card.label}</p>
              </div>
              <p className="text-3xl font-bold" style={{ color: card.color, fontFamily: "'Poppins', sans-serif" }}>{card.value}</p>
            </div>
          );
        })}
      </div>

      {/* Info */}
      <div className="bg-white rounded-xl p-5" style={{ border: "1px solid #e5e7eb" }}>
        <h3 className="font-bold text-gray-800 mb-3">Informasi Visitor Counter</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li>&#x2022; Counter bertambah setiap kali ada pengunjung membuka website.</li>
          <li>&#x2022; Counter &ldquo;Hari Ini&rdquo; direset otomatis setiap tengah malam.</li>
          <li>&#x2022; Data disimpan di database server dan tidak akan hilang saat server restart.</li>
          <li>&#x2022; Klik <strong>Refresh</strong> untuk memperbarui data.</li>
        </ul>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { isAuthed, username, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("statistik");

  useEffect(() => {
    if (!isAuthed) navigate("/admin/login");
  }, [isAuthed, navigate]);

  if (!isAuthed) return null;

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F8FAF8" }}>
      {/* Top bar */}
      <header className="text-white" style={{ backgroundColor: "#044712" }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Logo" className="h-9 w-auto bg-white rounded p-0.5" />
            <div>
              <p className="font-bold text-sm leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>Panel Admin Tozan</p>
              <p className="text-xs" style={{ color: "#bbf7d0" }}>Halo, {username}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold" style={{ backgroundColor: "rgba(255,255,255,0.12)" }}>
              <ExternalLink size={15} /> <span className="hidden sm:inline">Lihat Situs</span>
            </a>
            <button onClick={doLogout} data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold" style={{ backgroundColor: "#d4af37", color: "#044712" }}>
              <LogOut size={15} /> Keluar
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b" style={{ borderColor: "#dcfce7" }}>
        <div className="max-w-6xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const isActive = active === t.key;
            return (
              <button key={t.key} onClick={() => setActive(t.key)} data-testid={`tab-${t.key}`}
                className="flex items-center gap-2 px-3 sm:px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors"
                style={{ color: isActive ? "#198530" : "#6b7280", borderColor: isActive ? "#198530" : "transparent" }}>
                <t.icon size={16} /> <span className="hidden sm:inline">{t.label}</span><span className="sm:hidden">{t.label.split(" ")[0]}</span>
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {active === "statistik"   && <StatistikTab />}
        {active === "artikel"     && <ArticlesAdmin />}
        {active === "galeri"      && <GalleryAdmin />}
        {active === "organisasi"  && <OrgAdmin />}
        {active === "pendaftaran" && <RegistrationAdmin />}
        {active === "pengaturan"  && <SettingsAdmin />}
      </main>
    </div>
  );
}
